/**
 * Hook para gerenciar eventos WebSocket de mensagens
 *
 * Integra com o SocketService existente para receber atualizacoes em tempo real
 * Mantem fallback para polling caso WebSocket nao esteja disponivel
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { socketService } from '../lib/socketService';
import { Message } from '../types/messages';
import { sendToTerminal } from '../lib/terminalLogger';
import { normalizeMessage } from '../services/messagesService';
import { useWebSocketConnection } from '../contexts/WebSocketContext';
import { messageKeys } from './useMessagesQueries';

const LOG_PREFIX = '[MESSAGES_WEBSOCKET]';

interface MessageNewEvent {
  message: Message;
  unreadCount: number | null;
}

interface MessageReadEvent {
  messageId: number;
  unreadCount: number;
}

interface MessageUpdatedEvent {
  message: Message;
}

interface MessageDeletedEvent {
  messageId: number;
  unreadCount: number | null;
}

interface MessageReplyReceivedEvent {
  messageId: number;
  reply: {
    id: number;
    mensagem: string;
    tipo: string;
    criada_em: string;
  };
  userId: number;
  userName: string;
}

/**
 * Hook para gerenciar eventos WebSocket de mensagens
 */
export function useMessagesWebSocket(
  userId: number | null | undefined,
  options: {
    onNewMessage?: (message: Message, unreadCount: number | null) => void;
    onMessageRead?: (messageId: number, unreadCount: number) => void;
    onMessageUpdated?: (message: Message) => void;
    onMessageDeleted?: (messageId: number, unreadCount: number | null) => void;
    onReplyReceived?: (event: MessageReplyReceivedEvent) => void;
  } = {}
) {
  const { isConnected, reconnect, isEnabled } = useWebSocketConnection();
  const handlersRef = useRef(options);
  const queryClient = useQueryClient();
  const waitingEnableLoggedRef = useRef(false);

  // Atualizar handlers quando mudarem
  useEffect(() => {
    handlersRef.current = options;
  }, [options]);

  // Configurar listeners WebSocket
  useEffect(() => {
    if (!userId) {
      waitingEnableLoggedRef.current = false;
      return;
    }

    if (!isEnabled) {
      if (!waitingEnableLoggedRef.current) {
        sendToTerminal(`⏸️ ${LOG_PREFIX} WebSocket aguardando habilitação central`, {
          userId,
        });
        waitingEnableLoggedRef.current = true;
      }
      return;
    }

    waitingEnableLoggedRef.current = false;

    const socket = socketService.getSocket({
      source: 'hook-messages',
      reason: 'register-message-listeners',
    });
    if (!socket) {
      sendToTerminal(`[WS] ${LOG_PREFIX} WebSocket indisponível por configuração`, {
        userId,
        error: socketService.getConfigurationError(),
      });
      return;
    }

    const handleNewMessage = (data: MessageNewEvent) => {
      try {
        sendToTerminal(`[WS] ${LOG_PREFIX} message:new`, {
          messageId: data.message.id,
          titulo: data.message.titulo,
          unreadCount: data.unreadCount
        });

        const normalizedMessage = normalizeMessage(data.message);

        if (handlersRef.current.onNewMessage) {
          handlersRef.current.onNewMessage(normalizedMessage, data.unreadCount);
        }

        // Invalidar apenas listas (não detalhes de mensagens individuais)
        queryClient.invalidateQueries({ queryKey: messageKeys.lists() });

        if (data.unreadCount !== null) {
          queryClient.setQueryData(messageKeys.unreadCount, { unread_count: data.unreadCount });
        }
      } catch (error) {
        sendToTerminal(`[WS] ${LOG_PREFIX} ERRO em message:new`, { error: String(error) });
        queryClient.invalidateQueries({ queryKey: messageKeys.all });
      }
    };

    const handleMessageRead = (data: MessageReadEvent) => {
      try {
        sendToTerminal(`[WS] ${LOG_PREFIX} message:read`, {
          messageId: data.messageId,
          unreadCount: data.unreadCount
        });

        if (handlersRef.current.onMessageRead) {
          handlersRef.current.onMessageRead(data.messageId, data.unreadCount);
        }

        // Invalidar apenas listas (contagem já tratada via setQueryData)
        queryClient.invalidateQueries({ queryKey: messageKeys.lists() });
        queryClient.setQueryData(messageKeys.unreadCount, { unread_count: data.unreadCount });
      } catch (error) {
        sendToTerminal(`[WS] ${LOG_PREFIX} ERRO em message:read`, { error: String(error) });
        queryClient.invalidateQueries({ queryKey: messageKeys.all });
      }
    };

    const handleMessageUpdated = (data: MessageUpdatedEvent) => {
      try {
        sendToTerminal(`[WS] ${LOG_PREFIX} message:updated`, {
          messageId: data.message.id,
          titulo: data.message.titulo
        });

        const normalizedMessage = normalizeMessage(data.message);

        if (handlersRef.current.onMessageUpdated) {
          handlersRef.current.onMessageUpdated(normalizedMessage);
        }

        // Invalidar listas + detalhes (conteúdo da mensagem pode ter mudado)
        queryClient.invalidateQueries({ queryKey: messageKeys.lists() });
        queryClient.invalidateQueries({ queryKey: messageKeys.details() });
      } catch (error) {
        sendToTerminal(`[WS] ${LOG_PREFIX} ERRO em message:updated`, { error: String(error) });
        queryClient.invalidateQueries({ queryKey: messageKeys.all });
      }
    };

    const handleMessageDeleted = (data: MessageDeletedEvent) => {
      try {
        sendToTerminal(`[WS] ${LOG_PREFIX} message:deleted`, {
          messageId: data.messageId,
          unreadCount: data.unreadCount
        });

        if (handlersRef.current.onMessageDeleted) {
          handlersRef.current.onMessageDeleted(data.messageId, data.unreadCount);
        }

        // Invalidar apenas listas (mensagem removida)
        queryClient.invalidateQueries({ queryKey: messageKeys.lists() });

        if (data.unreadCount !== null) {
          queryClient.setQueryData(messageKeys.unreadCount, { unread_count: data.unreadCount });
        }
      } catch (error) {
        sendToTerminal(`[WS] ${LOG_PREFIX} ERRO em message:deleted`, { error: String(error) });
        queryClient.invalidateQueries({ queryKey: messageKeys.all });
      }
    };

    const handleReplyReceived = (data: MessageReplyReceivedEvent) => {
      try {
        sendToTerminal(`[WS] ${LOG_PREFIX} message:reply_received`, {
          messageId: data.messageId,
          userId: data.userId,
          userName: data.userName
        });

        if (handlersRef.current.onReplyReceived) {
          handlersRef.current.onReplyReceived(data);
        }

        queryClient.invalidateQueries({ queryKey: messageKeys.detail(data.messageId) });
      } catch (error) {
        sendToTerminal(`[WS] ${LOG_PREFIX} ERRO em message:reply_received`, { error: String(error) });
        queryClient.invalidateQueries({ queryKey: messageKeys.all });
      }
    };

    socket.on('message:new', handleNewMessage);
    socket.on('message:read', handleMessageRead);
    socket.on('message:updated', handleMessageUpdated);
    socket.on('message:deleted', handleMessageDeleted);
    socket.on('message:reply_received', handleReplyReceived);

    return () => {
      socket.off('message:new', handleNewMessage);
      socket.off('message:read', handleMessageRead);
      socket.off('message:updated', handleMessageUpdated);
      socket.off('message:deleted', handleMessageDeleted);
      socket.off('message:reply_received', handleReplyReceived);
    };
  }, [userId, queryClient, isEnabled]);

  return {
    isConnected,
    useWebSocket: isConnected,
    reconnect
  };
}
